import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Dropdown, Form, Input, Message } from 'semantic-ui-react';
import { SG_GET_ORGANIZATION_SELECT, SG_GET_EMPLOYEE_SELECT_LIST, SG_PREGENERATE_STAFF_REPORT } from 'constants/actions';

const PreGenerateModal = ({ open, onClose, defaultOrg = null, defaultEmp = null }) => {
  const dispatch = useDispatch();

  const organizations = useSelector((s) => s.organizations.organization_select || []);
  const employeeSelect = useSelector((s) => s.employees.employee_select_list || []);

  const [orgId, setOrgId] = useState(defaultOrg);
  const [empId, setEmpId] = useState(defaultEmp);
  const [auditType, setAuditType] = useState(3);
  const [statusMsg, setStatusMsg] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pregenResult = useSelector((s) => s.staff_reports.pregen_result);
  const pending = useSelector((s) => s.staff_reports.pending);
  const lastError = useSelector((s) => s.staff_reports.last_error);

  useEffect(() => {
    if (open) {
      // ensure organizations are loaded
      dispatch({ type: SG_GET_ORGANIZATION_SELECT });
      // reset local state when modal opens
      setOrgId(defaultOrg);
      setEmpId(defaultEmp);
      setAuditType(3);
      setStatusMsg(null);
    }
  }, [open, dispatch, defaultOrg, defaultEmp]);

  useEffect(() => {
    if (orgId) {
      // get employee select list for this organization
      dispatch({ type: SG_GET_EMPLOYEE_SELECT_LIST, payload: `organization=${orgId}` });
    }
  }, [orgId, dispatch]);

  const orgOptions = organizations.map((o) => ({ key: o.id, text: `${o.id} — ${o.name}`, value: o.id }));
  const empOptions = (employeeSelect || []).map((e) => ({ key: e.id, text: `${e.full_name || `${e.first_name || ''} ${e.last_name || ''}` } (${e.email || e.id})`, value: e.id }));

  function handleConfirm() {
    if (!orgId || !empId) {
      setStatusMsg({ type: 'warning', text: 'Please choose both organization and employee' });
      return;
    }

    setStatusMsg({ type: 'info', text: 'Queuing pre-generate...' });
    setIsSubmitting(true);
    dispatch({ type: SG_PREGENERATE_STAFF_REPORT, payload: { organization_id: Number(orgId), employee_id: Number(empId), audit_type_id: Number(auditType) } });
  }

  useEffect(() => {
    // when pregen result updates, show it (and stop submitting)
    if (isSubmitting && pregenResult) {
      // pregenResult may be an object describing the cached report or a message
      setIsSubmitting(false);
      setStatusMsg({ type: 'success', text: `Queued — ${pregenResult.task_id ? `Task: ${pregenResult.task_id}` : 'success' } ${pregenResult.cache_key ? ` | Key: ${pregenResult.cache_key}` : ''}` });
    }
  }, [isSubmitting, pregenResult]);

  useEffect(() => {
    if (isSubmitting && lastError && lastError.op === 'PREGENERATE') {
      setIsSubmitting(false);
      setStatusMsg({ type: 'warning', text: `Error: ${lastError.error?.message || lastError.error}` });
    }
  }, [isSubmitting, lastError]);

  // show a nice pending message when saga reports pending
  useEffect(() => {
    if (pending && isSubmitting) {
      setStatusMsg({ type: 'info', text: 'Generating report — please wait...' });
    }
  }, [pending, isSubmitting]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Pre-generate Report</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <label>Organization</label>
            <Dropdown
              placeholder="Select organization"
              fluid
              search
              selection
              options={orgOptions}
              value={orgId}
              onChange={(_, { value }) => setOrgId(value)}
            />
          </Form.Field>

          <Form.Field>
            <label>Employee</label>
            <Dropdown
              placeholder="Select employee"
              fluid
              search
              selection
              options={empOptions}
              value={empId}
              onChange={(_, { value }) => setEmpId(value)}
              disabled={!orgId}
            />
          </Form.Field>

          <Form.Field>
            <label>Audit type id</label>
            {/* audit type is generally defaulted by the system — make read-only to avoid accidental edits */}
            <Input value={auditType} readOnly disabled style={{ opacity: 0.85 }} />
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>Audit ID is fixed for reports (default: 3). Change only if you know what you're doing.</div>
          </Form.Field>

          {statusMsg ? (
            <Message
              success={statusMsg.type === 'success'}
              warning={statusMsg.type === 'warning'}
              info={statusMsg.type === 'info'}
            >
              {statusMsg.text}
            </Message>
          ) : null}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={pending}>Cancel</Button>
        <Button primary onClick={handleConfirm} disabled={!orgId || !empId || pending}>Pre-generate</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default PreGenerateModal;
